import React, { useEffect, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  autocompleteClasses,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useThemeContext } from "../../../Context/ThemeContext";
import ArrowButton from "../../../Components/Common/ArrowButton";
import { DARKCOLORS, LIGHTCOLORS, ROUTES } from "../../../Utils/constants";
import { getTodaySalesSummary } from "../../../Services/authService";
import { format, parseISO } from "date-fns";
import "./SalesAnalysis.css";
import { de, enUS } from "date-fns/locale";
// import "../../App.css";

export default function SalesAnalysis() {
  const { i18n, t } = useTranslation();
  const { mode } = useThemeContext();

  const borderColor1 =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;

  const [view, setView] = useState("day");
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(false);

  // True only when there is at least one non-zero sales value
  const hasSalesData =
    salesData.length > 0 && salesData.some((d) => Number(d.value) > 0);

  useEffect(() => {
    fetchSalesData(view);
  }, [view]);

  const fetchSalesData = async (rangeType) => {
    try {
      setLoading(true);
      const today = format(new Date(), "yyyy-MM-dd");
      const data = await getTodaySalesSummary(rangeType, today, today);
      setSalesData(data || []);
    } catch (err) {
      console.error(err.message || t("dashboard.errorFetchingSalesData"));
      setSalesData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewChange = (viewType) => {
    setView(viewType);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          elevation={3}
          sx={{
            padding: "8px 12px",
            backgroundColor: "#2a2a2a",
            color: "#fff",
            borderRadius: "8px",
          }}
        >
          <Typography variant="body2">{`€ ${payload[0].value}`}</Typography>
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {view === "week"
              ? t(`dashboard.${label}`)
              : view === "month"
                ? `${t("dashboard.week")} ${label.split(" ")[1]}`
                : view === "year"
                  ? t(`dashboard.${label}`)
                  : label}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  const getLocale = () => {
    switch (i18n.language) {
      case "de":
        return de;
      case "en":
      default:
        return enUS;
    }
  };

  const formatTickLabel = (tick) => {
    if (view === "week") {
      return t(`dashboard.${tick}`) || tick;
    } else if (view === "month") {
      const [, number] = tick.split(" ");
      return number;
    } else if (view === "day") {
      return tick.slice(0, 2);
    }

    return tick;
  };

  return (
    <Box>
      {/* Header */}
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <h3 className="subheading-sec-card" style={{ margin: "0px" }}>
          {t("dashboard.salesAnalysis")}
        </h3>
        <Box className=" arrow-mobile">
          <ArrowButton targetRoute={ROUTES.SALES_ANALYTICS} />
        </Box>
        <Box
          className="home-sale-days-div"
          sx={{ display: "flex", gap: 6, alignItems: "center" }}
        >
          {/* View Toggle (Day, Week, Month) */}
          <Box sx={{ display: "flex", gap: 3, cursor: "pointer" }}>
            {["day", "week", "month"].map((type) => (
              <Typography
                key={type}
                onClick={() => handleViewChange(type)}
                sx={{
                  borderBottom:
                    view === type ? `2px solid ${borderColor1}` : "none",
                }}
              >
                {t(`dashboard.${type}`)}
              </Typography>
            ))}
          </Box>
          <Box className="home-sales-arrow">
            <ArrowButton targetRoute={ROUTES.SALES_ANALYTICS} />
          </Box>
        </Box>
      </Grid>

      {/* Chart */}
      <Box className="sales-chart-section">
        <Box
          className="sales-chart-container"
          sx={{
            width: "100%",
            height: "300px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          {loading ? (
            <CircularProgress color="primary" />
          ) : (
            <>
              <ResponsiveContainer>
                <AreaChart data={salesData}>
                  <defs>
                    <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4a90e2" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#4a90e2" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="day"
                    stroke="#aaa"
                    tickLine={false}
                    axisLine={{ stroke: "#aaa" }}
                    padding={{ left: 10 }}
                    tickFormatter={formatTickLabel}
                    fontSize={"14px"}
                    label={{
                      value:
                        view === "day"
                          ? t("dashboard.hours")
                          : view === "week"
                            ? t("dashboard.day")
                            : view === "month"
                              ? t("dashboard.week")
                              : undefined,
                      position: "insideBottom",
                      offset:-2,
                      fill: "#888",
                      fontSize:"14px"
                    }}
                  />
                  <YAxis
                    width={60}
                    stroke="#aaa"
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) => value}
                    padding={{ bottom: 10 }}
                    domain={hasSalesData ? [0, "auto"] : [0, 10]}
                    tickCount={hasSalesData ? undefined : 6}
                    fontSize={"14px"}
                    label={{
                      value: "€",
                      position: "insideLeft",
                      offset: 15,
                      style: {
                        fill: "#aaa",
                        fontSize: 16,
                        fontWeight: 600,
                      },
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  {hasSalesData && (
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="#5E5CE6"
                      strokeWidth={2}
                      fill="url(#gradient)"
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
              {!hasSalesData && (
                <Typography
                  color="gray"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  {t("dashboard.noSalesDataForPeriod")}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
